import Column from '@app/components/layout/Column'
import { useTranslations } from '@i18n/useLanguage'
import { useCallback, useEffect, useState } from 'react'
import { useClient } from '@based/sdk/react'
import { useToast } from '@app/util/hooks/useToast'
import { EditContainer } from './blocks/shared/EditContainer'
import { useEditContainerClose } from './blocks/shared/useEditContainerClose'
import { FieldEditActions } from './blocks/shared/FieldEditActions'
import { Text } from '@app/components/core/typography/Text'
import { AmenitiesSelectionGrid } from '@app/pages/dashboard/initiative/shared/forms/amenities/AmenitiesSelectionGrid'
import { InitiativeWithId } from '../../../shared/initiativeTypes'

export type AmenitiesEditProps = {
  initiative: InitiativeWithId
}

const AmenitiesEditInner = ({ initiative }: AmenitiesEditProps) => {
  const close = useEditContainerClose()
  if (!close) {
    throw new Error('AmenitiesEditInner requires an EditContainer ancestor')
  }

  const translationsRoot = useTranslations().app
  const common = translationsRoot.common

  const [selectedIds, setSelectedIds] = useState<number[]>(() =>
    (initiative.amenities ?? []).map((a) => a.id),
  )

  const client = useClient()
  const { showFailToast, showSuccessToast } = useToast()

  useEffect(() => {
    setSelectedIds((initiative.amenities ?? []).map((a) => a.id))
  }, [initiative.amenities])

  const save = useCallback(() => {
    void client
      .call('edit-initiative', {
        id: initiative.id,
        value: {
          amenities: selectedIds.map((id) => ({ id })),
        },
      })
      .then((success) => {
        if (success) {
          showSuccessToast(common.toastSuccess)
          close()
        } else {
          showFailToast()
        }
      })
      .catch(() => showFailToast())
  }, [
    client,
    initiative.id,
    selectedIds,
    close,
    showFailToast,
    showSuccessToast,
    common.toastSuccess,
  ])

  return (
    <Column gap="1.5rem" alignItems="stretch">
      <AmenitiesSelectionGrid
        initiativeId={initiative.id}
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
        variant="wide"
      />
      <FieldEditActions
        onCancel={() => {
          setSelectedIds((initiative.amenities ?? []).map((a) => a.id))
          close()
        }}
        onSave={save}
      />
    </Column>
  )
}

const AmenitiesView = ({ initiative }: AmenitiesEditProps) => {
  const emptyLabel = useTranslations().app.common.notSpecified
  const names = (initiative.amenities ?? [])
    .map((a) => a.name)
    .filter((n) => (n ?? '').trim() !== '')

  return (
    <Text.Medium color="var(--palette-blue-500)">
      {names.length > 0 ? names.join(', ') : emptyLabel}
    </Text.Medium>
  )
}

export const AmenitiesEdit = ({ initiative }: AmenitiesEditProps) => {
  const view = useTranslations().app.dashboard.initiative.view

  return (
    <EditContainer title={view.amenitiesSection}>
      <EditContainer.View>
        <AmenitiesView initiative={initiative} />
      </EditContainer.View>
      <EditContainer.Edit>
        <AmenitiesEditInner initiative={initiative} />
      </EditContainer.Edit>
    </EditContainer>
  )
}
